import { CategoryFilter } from '@/components';
import CourseLibraryHeader from '@/components/CourseLibraryHeader';
import NoResult from '@/components/NoModulesResult';
import { CourseLibraryPermissions } from '@/lib/auth/permissions/courseLibraryPermissions.enum';
import { useUserHasCapabilities } from '@/lib/auth/useUserHasCapabilities';
import { getQueryString } from '@/lib/util';
import {
  useGetCurrentTenantProfileQuery,
  useGetElearningCategoriesPerTenantQuery,
  useGetVideoLibraryElearningsQuery,
  useGetVideoLibraryElearningV2Query,
} from '@/modules/services';
import { PaginationType } from '@/modules/types/elearning';
import { Layout, Spinner } from 'elmo-elements';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useMemo } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useHistory, useLocation } from 'react-router-dom';
import { CourseLibraryDefaultQueryString } from '../CourseLibrary/CourseLibraryRoutes';
import {
  CourseLibraryViewData,
  LibraryElearningsDataType,
} from '../CourseLibrary/types';
import MyLibraryRoutes from '../MyLibrary/MyLibraryRoutes';
import VideoLibraryList from './components/VideoLibraryList';
import VideoLibraryListV2 from './components/VideoLibraryView';

const VideoLibrary = (): React.ReactElement => {
  const location = useLocation();
  const history = useHistory();
  const currentQueryString = getQueryString(
    location.search,
    CourseLibraryDefaultQueryString,
  );
  const { learningElaNewCourseLibraryEnable: isElaCourseLibraryEnabled } =
    useFlags();

  const {
    data: videoLibraryData,
    isLoading,
    isError,
    error,
  } = useGetVideoLibraryElearningsQuery(currentQueryString, {
    skip: isElaCourseLibraryEnabled !== false,
  });
  const { data: videoLibraryViewData } = useGetVideoLibraryElearningV2Query(
    getQueryString(location.search, CourseLibraryDefaultQueryString),
    { skip: !isElaCourseLibraryEnabled },
  );
  const { data: tenantData, isLoading: tenantIsLoading } =
    useGetCurrentTenantProfileQuery();
  const { data: elaCategoriesPerTenant } =
    useGetElearningCategoriesPerTenantQuery(undefined, {
      skip: !isElaCourseLibraryEnabled,
    });

  const allowedCategories = useMemo(() => {
    if (videoLibraryData?.allowedCategories) {
      return videoLibraryData.allowedCategories;
    }

    if (elaCategoriesPerTenant?.categories) {
      return elaCategoriesPerTenant.categories.map((category) => ({
        categoryId: category.id,
        categoryName: category.name,
      }));
    }

    return [];
  }, [videoLibraryData, elaCategoriesPerTenant]);

  const shouldShowCategoryFilter = useMemo(() => {
    // For legacy course library
    if (
      videoLibraryData?.allowedCategories &&
      videoLibraryData?.allowedCategories?.length > 0
    ) {
      return true;
    }

    // For new ELA course library
    if (
      videoLibraryViewData &&
      elaCategoriesPerTenant?.categories &&
      elaCategoriesPerTenant?.categories?.length > 0
    ) {
      return true;
    }

    return false;
  }, [videoLibraryData, videoLibraryViewData, elaCategoriesPerTenant]);

  const videoLibraryEnabled =
    tenantData && +tenantData.configMetadata.enableVideoLibrary > 0;
  const canAccessVideoLibrary = useUserHasCapabilities([
    CourseLibraryPermissions.VIEW_VIDEO_LIBRARY,
  ]);

  const renderLibraryList = (data: LibraryElearningsDataType | undefined) =>
    data && data.elearnings.length <= 0 ? <NoResult /> : <VideoLibraryList />;
  const renderLibraryListV2 = (
    data: PaginationType<CourseLibraryViewData> | undefined,
  ) => (data && data.data.length <= 0 ? <NoResult /> : <VideoLibraryListV2 />);

  const handleError = useErrorHandler();

  // Wait for the flag: rendering the legacy list early would call the old course builder.
  if (isElaCourseLibraryEnabled === undefined || isLoading || tenantIsLoading) {
    return <Spinner />;
  }

  if (
    (videoLibraryEnabled !== undefined && !videoLibraryEnabled) ||
    (canAccessVideoLibrary !== undefined && !canAccessVideoLibrary)
  ) {
    history.replace(MyLibraryRoutes.path);
    return <div />;
  }

  if (isError) {
    handleError(error);
    return <div />;
  }

  return (
    <>
      <Layout.Header>
        <CourseLibraryHeader title="Video Library" />
      </Layout.Header>
      <Layout.Content>
        {shouldShowCategoryFilter && (
          <CategoryFilter
            allowedCategories={allowedCategories}
            isNewList={isElaCourseLibraryEnabled}
            isLibraryDataLoading={isLoading}
          />
        )}
        {isElaCourseLibraryEnabled
          ? renderLibraryListV2(videoLibraryViewData)
          : renderLibraryList(videoLibraryData)}
      </Layout.Content>
    </>
  );
};

export default VideoLibrary;
